/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Spin,
  Form,
} from 'antd';

import Table from 'components/Table';
import Select from 'components/Select';

import { formatReceived } from 'utils/formatValues';
import { checkToken } from 'api/utils';
import currencyFormatter from 'utils/currencyFormatter';

import { getKardexSbcEmpleadosVistaOptimized } from 'api/catalogos/KardexSBCEmpleado';
import { getEstadosGlobales } from 'api/catalogos/index';
import { getEmpleadosOptimized } from 'api/estructuraOrganizacional/empleados';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import { getSBCBimestres } from 'api/catalogos/SBCBimentre';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
} from 'utils/constants';
import moment from 'moment';

export const kardexSbcEmpleadoPermission = {
  permissionModel: 'kardexsbcempleado',
  catalogsModels: [],
  tabsModels: [],
};

const KardexSbcEmpleado = ({ permission }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [bimestres, setBimestres] = useState([]);
  const [estados, setEstados] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedData, setSelectedData] = useState(null);

  // Trae el Kárdex scoped por los filtros capturados (nunca sin filtrar: el
  // query sin filtros regresa TODO el historial de la Entidad sin paginar).
  const fetchData = async (filtros) => {
    const {
      empleado, periodo_fiscal: periodoFiscal, bimestre, estados_globales: estadosGlobales,
    } = filtros || form.getFieldsValue();
    if (!empleado && !periodoFiscal && !bimestre && !estadosGlobales) {
      setData([]);
      return;
    }
    setLoading(true);
    const params = {};
    if (empleado) params.empleado_id = empleado;
    if (periodoFiscal) params.periodo_fiscal_id = periodoFiscal;
    if (bimestre) params.bimestre_id = bimestre;
    if (estadosGlobales) params.estados_globales_id = estadosGlobales;
    const response = await getKardexSbcEmpleadosVistaOptimized(params);
    setData(formatReceived(Array.isArray(response) ? response : []));
    setSelectedRowKeys([]);
    setSelectedData(null);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchCatalogos = async () => {
      setLoading(true);
      await checkToken();
      const [_empleados, _periodosFiscales, _bimestres, _estados] = await Promise.all([
        getEmpleadosOptimized(),
        getPeriodosFiscales({ estados_globales: ESTATUS_AUTORIZADO }),
        getSBCBimestres(),
        getEstadosGlobales(),
      ]);
      if (mounted) {
        setEmpleados(Array.isArray(_empleados) ? _empleados : _empleados?.results || []);
        setPeriodosFiscales(_periodosFiscales);
        setBimestres(_bimestres);
        setEstados(_estados);
        // Por defecto se muestran los Kárdex Activos (sin esto la tabla
        // arranca vacía porque fetchData exige al menos un filtro).
        form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
        await fetchData({ estados_globales: ESTATUS_ACTIVO });
      }
    };
    fetchCatalogos();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeFiltro = () => {
    fetchData();
  };

  const onChangePeriodoFiscal = () => {
    // El Bimestre seleccionado puede no pertenecer al nuevo Periodo Fiscal.
    form.setFieldValue('bimestre', undefined);
    fetchData();
  };

  const periodoFiscalSeleccionado = Form.useWatch('periodo_fiscal', form);
  const bimestresFiltrados = periodoFiscalSeleccionado
    ? bimestres.filter((b) => b.periodo_fiscal === periodoFiscalSeleccionado)
    : bimestres;
  // El filtro de Estatus solo ofrece Activo/Cancelado: el Kárdex no se
  // Autoriza/Cancela manualmente desde esta pantalla (ver Autorizar SBC
  // Variable), así que "Autorizado"/"Inactivo" no aplican aquí.
  const estadosFiltro = estados.filter(
    (e) => [ESTATUS_ACTIVO, ESTATUS_CANCELADO].includes(e.id),
  );

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData(record);
  };

  const columns = [
    {
      titleText: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 120,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Movimiento',
      dataIndex: 'movimiento_sbc_imss_descripcion',
      key: 'movimiento_sbc_imss_descripcion',
      width: 200,
    },
    {
      titleText: 'No. de Empleado',
      // El backend regresa `empleado_numero_de_empleado`, no `empleado_numero`
      // -- con el dataIndex anterior esta columna siempre salía vacía.
      dataIndex: 'empleado_numero_de_empleado',
      key: 'empleado_numero_de_empleado',
      width: 140,
    },
    {
      titleText: 'Empleado',
      dataIndex: 'empleado_numero_y_nombre_completo',
      key: 'empleado_numero_y_nombre_completo',
      width: 220,
    },
    {
      titleText: 'Ejercicio',
      dataIndex: 'periodo_fiscal_periodo',
      key: 'periodo_fiscal_periodo',
      width: 100,
    },
    {
      titleText: 'Bimestre',
      dataIndex: 'bimestre_nombre',
      key: 'bimestre_nombre',
      width: 150,
      render: (val, record) => (val ? `${val} (${record?.meses_del_bimestre || ''})` : ''),
    },
    {
      titleText: 'Plaza / Puesto',
      dataIndex: 'clave_y_descripcion',
      key: 'clave_y_descripcion',
      width: 250,
      render: (val) => val || '---',
    },
    {
      titleText: 'Área',
      dataIndex: 'clave_y_nombre',
      key: 'clave_y_nombre',
      width: 220,
    },
    {
      titleText: 'Sueldo Diario',
      dataIndex: 'sueldo_diario',
      key: 'sueldo_diario',
      width: 130,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Factor SBC',
      dataIndex: 'factor_sbc',
      key: 'factor_sbc',
      width: 110,
      align: 'right',
      render: (val) => (val !== null && val !== undefined ? Number(val).toFixed(5) : ''),
    },
    {
      titleText: 'SBC Sueldo Fijo',
      dataIndex: 'sbc_sueldo_fijo',
      key: 'sbc_sueldo_fijo',
      width: 140,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'SBC Sueldo Variable',
      dataIndex: 'sbc_sueldo_variable',
      key: 'sbc_sueldo_variable',
      width: 160,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Tope SBC',
      dataIndex: 'tope_sbc',
      key: 'tope_sbc',
      width: 130,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'SBC',
      dataIndex: 'sbc',
      key: 'sbc',
      width: 120,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales_descripcion',
      key: 'estados_globales_descripcion',
      width: 130,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Form layout="vertical" form={form} name="kardex_sbc_filtros">
          <Row gutter={10}>
            <Col xs={24} sm={8}>
              <Form.Item name="empleado" label="No. de Empleado">
                <Select
                  keyProp="id"
                  labelProp="numero_y_nombre_completo"
                  dataSource={empleados}
                  allowClear
                  onChange={onChangeFiltro}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="periodo_fiscal" label="Periodo Fiscal">
                <Select
                  dataSource={periodosFiscales}
                  allowClear
                  onChange={onChangePeriodoFiscal}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="bimestre" label="Bimestre">
                <Select
                  keyProp="id"
                  dataSource={bimestresFiltrados}
                  render={(value) => `${value?.nombre} (${value?.periodo_descripcion})`}
                  allowClear
                  onChange={onChangeFiltro}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="estados_globales" label="Estatus">
                <Select
                  keyProp="id"
                  labelProp="descripcion"
                  dataSource={estadosFiltro}
                  allowClear
                  onChange={onChangeFiltro}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Table
          cols={columns}
          data={data}
          allowExportPDF={false}
          permission={permission}
          rowSelection={{
            selectedRowKeys,
            type: 'radio',
          }}
          handleOnRowClick={handleOnRowClick}
          disableDelete
        />
      </Spin>
    </Row>
  );
};

export default KardexSbcEmpleado;
